import { useState, useEffect } from 'react';
import { useAuth } from '../hooks/useAuth';
import { useToast } from '../hooks/useToast';
import { useLanguage } from '../hooks/useLanguage';
import { Button } from './Button';

export const Navbar = ({ toggleSidebar }) => {
  const { user, logout } = useAuth();
  const toast = useToast();
  const { language, toggleLanguage, t } = useLanguage();
  const [logo, setLogo] = useState(null);
  const [appTitle, setAppTitle] = useState('');

  useEffect(() => {
    // Load logo and title from localStorage
    const savedLogo = localStorage.getItem('app_logo');
    const savedTitle = localStorage.getItem('app_title');
    
    if (savedLogo) {
      setLogo(savedLogo);
    }
    if (savedTitle) {
      setAppTitle(savedTitle);
    }

    // Listen for storage changes (when logo/title is updated in settings)
    const handleStorageChange = () => {
      const updatedLogo = localStorage.getItem('app_logo');
      const updatedTitle = localStorage.getItem('app_title');
      setLogo(updatedLogo);
      setAppTitle(updatedTitle || '');
    };

    window.addEventListener('storage', handleStorageChange);
    // Custom events for same-tab updates
    window.addEventListener('logoUpdated', handleStorageChange);
    window.addEventListener('titleUpdated', handleStorageChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('logoUpdated', handleStorageChange);
      window.removeEventListener('titleUpdated', handleStorageChange);
    };
  }, []);

  const handleLogout = async () => {
    await logout();
    toast.success(t('loggedOutSuccess'));
  };

  return (
    <nav className="bg-white shadow-md fixed top-0 left-0 right-0 z-40 h-16">
      <div className="h-full px-2 sm:px-4 flex items-center justify-between">
        <div className="flex items-center gap-2 sm:gap-4">
          <button
            onClick={toggleSidebar}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            aria-label={t('toggleSidebar')}
          >
            <svg
              className="w-6 h-6 text-gray-600"
              fill="none"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>

          {logo || appTitle ? (
            <div className="flex items-center gap-3">
              {logo && (
                <img 
                  src={logo} 
                  alt={appTitle || t('appName') || 'Lead Management System'} 
                  className="h-10 w-auto object-contain"
                />
              )}
              {appTitle && (
                <h1 className="text-base sm:text-xl font-bold text-gray-900 truncate">
                  {appTitle}
                </h1>
              )}
            </div>
          ) : (
            <h1 className="text-base sm:text-xl font-bold text-gray-900 truncate">
              <span className="hidden sm:inline">{t('appName') || 'Lead Management System'}</span>
              <span className="sm:hidden">{t('appNameShort') || 'Leads'}</span>
            </h1>
          )}
        </div>

        <div className="flex items-center gap-1 sm:gap-4">
          {/* Language Selector */}
          <button
            onClick={toggleLanguage}
            className="flex items-center gap-1 sm:gap-2 px-2 sm:px-3 py-2 hover:bg-gray-100 rounded-lg transition-colors"
            title={t('switchLanguage')}
          >
            <span className="text-lg">{language === 'en' ? '🇺🇸' : '🇮🇱'}</span>
            <span className="hidden sm:inline text-sm font-medium text-gray-700">
              {language === 'en' ? 'EN' : 'עב'}
            </span>
          </button>

          {/* User info - hidden on mobile */}
          <div className="hidden md:block text-right">
            <p className="text-sm font-medium text-gray-900">
              {user?.full_name || user?.email}
            </p>
            <p className="text-xs text-gray-500 capitalize">
              {user?.role || 'User'}
            </p>
          </div>

          <Button
            variant="outline"
            size="sm"
            onClick={handleLogout}
            className="hidden sm:inline-flex"
          >
            {t('logout')}
          </Button>

          {/* Mobile logout icon */}
          <button
            onClick={handleLogout}
            className="sm:hidden p-2 hover:bg-gray-100 rounded-lg transition-colors"
            title={t('logout')}
          >
            <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
            </svg>
          </button>
        </div>
      </div>
    </nav>
  );
};
